import {
  AlignmentNavigationCapability,
  FreeViewCapability,
  NavigationalCapabilityBase,
} from '@trimble/web3d-capabilities';
import { AlignmentNavigationStatus } from '@technology/web3d-plugin-trimbim';
import { CapabilityCoordinatorBase } from '../capability-coordinator-base';
import { CapabilityRegistry } from '../capability-registry/capability-registry';
import { CapabilityRegistryContainer } from '../capability-registry/capability-registry-container';
import { NavigationCapabilityCoordinator } from '../navigation-capability-coordinator';
import { Web3DViewer } from '@technology/web3d';
import { Constructor } from '../capability-registry/capability-registry-types';

export abstract class FitToViewBaseCapabilityCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  /**
   * Determines whether a pane is eligible for fit-to-view.
   * A pane is eligible if its active navigation capability is:
   * - FreeViewCapability (always eligible), or
   * - AlignmentNavigationCapability when not actively navigating.
   */
  protected isPaneEligibleForFitToView(
    pane: CapabilityRegistryContainer
  ): boolean {
    const activeCap = pane
      .get(NavigationCapabilityCoordinator)()
      ?.activeNavigationCapability();

    return (
      activeCap instanceof FreeViewCapability ||
      (activeCap instanceof AlignmentNavigationCapability &&
        activeCap.status() !== AlignmentNavigationStatus.Navigating)
    );
  }

  /**
   * Returns viewers from panes that are eligible for fit-to-view.
   */
  protected getEligibleViewers(): Web3DViewer[] {
    return this.registry
      .paneContainers()
      .map((pane) => {
        if (!this.isPaneEligibleForFitToView(pane)) return undefined;
        return pane.viewerWrapper()?.viewer;
      })
      .filter((v): v is Web3DViewer => v !== undefined);
  }

  protected getViewersToFit<T extends NavigationalCapabilityBase>(
    capabilities?: Constructor<T>[]
  ): Web3DViewer[] {
    return capabilities
      ? this.getViewersByActiveNavigationCapability(capabilities)
      : this.registry
          .paneContainers()
          .map((pane) => pane.viewerWrapper()?.viewer)
          .filter((viewer): viewer is Web3DViewer => viewer !== undefined);
  }

  private getViewersByActiveNavigationCapability<
    T extends NavigationalCapabilityBase,
  >(capabilities: Constructor<T>[]): Web3DViewer[] {
    return this.registry
      .paneContainers()
      .map((pane) => {
        const viewerWrapper = pane.viewerWrapper();
        if (viewerWrapper === undefined) return;

        const navCoordinator = pane.get(NavigationCapabilityCoordinator)();
        if (!navCoordinator) return;

        const navCapability = navCoordinator.activeNavigationCapability();
        const navInstance = capabilities.find(
          (c) => navCapability instanceof c
        );

        return navInstance ? viewerWrapper.viewer : undefined;
      })
      .filter((v): v is Web3DViewer => v !== undefined);
  }
}
